import { User } from '../../auth/SessionContext';
import { OrgInfo } from '../../auth/use-org-info/use-org-info';
import { getApiUrlFromWindowLocation } from '../../config/get-api-url';

const EXCLUDED_TENANT = '5fbb9c2c-c282-4048-b9ca-3c651f9a4346';

// Server mints the Intercom JWT with a 1h expiry; refetch before then, leaving a buffer.
const JWT_TTL_MS = 55 * 60 * 1000;

let cachedJwt: { userId: string; token: string; fetchedAt: number } | null = null;
let jwtFetchPromise: Promise<string | null> | null = null;
let jwtFetchUserId: string | null = null;

const fetchIntercomJwt = async (userId: string, token: string): Promise<string | null> => {
  if (cachedJwt && cachedJwt.userId === userId && Date.now() - cachedJwt.fetchedAt < JWT_TTL_MS) {
    return cachedJwt.token;
  }
  if (jwtFetchPromise && jwtFetchUserId === userId) return jwtFetchPromise;

  const promise = (async () => {
    try {
      const apiUrl = getApiUrlFromWindowLocation();
      const response = await fetch(`${apiUrl}/im/intercom/jwt`, {
        method: 'GET',
        headers: {
          Authorization: `JWT ${token}`,
        },
      });
      if (!response.ok) return null;
      const json = await response.json();
      const jwt: string | null = json?.data?.token ?? null;
      cachedJwt = jwt ? { userId, token: jwt, fetchedAt: Date.now() } : null;
      return jwt;
    } catch {
      return null;
    }
  })();

  jwtFetchPromise = promise;
  jwtFetchUserId = userId;
  try {
    return await promise;
  } finally {
    if (jwtFetchPromise === promise) {
      jwtFetchPromise = null;
      jwtFetchUserId = null;
    }
  }
};

export class Intercom {
  init = async (user?: User, org?: OrgInfo) => {
    if (!user || !org || org.tenantId === EXCLUDED_TENANT) {
      return;
    }

    const intercomJwt = await fetchIntercomJwt(user.userId, user.token);

    const w = window as any;
    if (w.Intercom) {
      /* eslint-disable @typescript-eslint/camelcase, camelcase */
      const bootSettings: Record<string, any> = {
        app_id: 'xbfhie8b',
        name: `${user.firstName} ${user.lastName}`,
        organisation: org.name,
        Privilege: user.userIdentityService.privLevel,
        created_at: user.dateCreated,
      };

      if (intercomJwt) {
        bootSettings.intercom_user_jwt = intercomJwt;
      } else {
        bootSettings.user_id = user.userId;
        bootSettings.email = user.email;
      }
      /* eslint-enable @typescript-eslint/camelcase, camelcase */

      w.Intercom('boot', bootSettings);
    }

  };
}
